import { useState, useEffect, useCallback, useRef } from 'react';
import { useOnlineStatus } from './useOnlineStatus';
import { QueuedRequest } from '../types/requestQueue';
import {
  changeProductStatus,
  updateShoppingList
} from '../api/shoppingList';

const STORAGE_KEY = 'request_queue';
const MAX_RETRY_COUNT = 3;

/**
 * Custom hook to manage offline request queue
 * Stores requests in localStorage and processes them when internet connection is restored
 */
export const useRequestQueue = (onQueueProcessed?: () => void) => {
  const isOnline = useOnlineStatus();
  const [queue, setQueue] = useState<QueuedRequest[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const processingRef = useRef<boolean>(false);
  const onQueueProcessedRef = useRef(onQueueProcessed);

  // Update callback ref when it changes
  useEffect(() => {
    onQueueProcessedRef.current = onQueueProcessed;
  }, [onQueueProcessed]);

  // Load queue from localStorage on mount
  useEffect(() => {
    const storedQueue = localStorage.getItem(STORAGE_KEY);
    if (storedQueue) {
      try {
        const parsedQueue = JSON.parse(storedQueue);
        setQueue(parsedQueue);
      } catch (error) {
        console.error('Failed to parse stored queue:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save queue to localStorage whenever it changes
  useEffect(() => {
    if (queue.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [queue]);

  const addToQueue = useCallback((request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retryCount'>) => {
    const queuedRequest: QueuedRequest = {
      ...request,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
    };

    setQueue((prev) => [...prev, queuedRequest]);
  }, []);

  const removeFromQueue = useCallback((requestId: string) => {
    setQueue((prev) => prev.filter((req) => req.id !== requestId));
  }, []);

  const executeRequest = useCallback(async (request: QueuedRequest): Promise<void> => {
    const { type, shoppingListId, payload } = request;

    switch (type) {
      case 'status-change':
        await changeProductStatus(shoppingListId, payload.productId, { status: payload.status });
        break;

      case 'list-update':
        await updateShoppingList(shoppingListId, payload);
        break;

      case 'product-add':
      case 'product-add-multiple':
      case 'product-delete':
      case 'product-edit':
        await updateShoppingList(shoppingListId, { products: payload.products });
        break;

      default:
        throw new Error(`Unknown request type: ${type}`);
    }
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current || queue.length === 0 || !isOnline) {
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);

    // Process requests one by one
    const currentQueue = [...queue];

    for (const request of currentQueue) {
      try {
        await executeRequest(request);
        // Remove successfully processed request
        removeFromQueue(request.id);
      } catch (error) {
        console.error('Failed to process queued request:', error);

        // Increment retry count
        const updatedRetryCount = request.retryCount + 1;

        if (updatedRetryCount >= MAX_RETRY_COUNT) {
          // Remove request after max retries
          console.error(`Request ${request.id} failed after ${MAX_RETRY_COUNT} retries, removing from queue`);
          removeFromQueue(request.id);
        } else {
          // Update retry count
          setQueue((prev) =>
            prev.map((req) =>
              req.id === request.id
                ? { ...req, retryCount: updatedRetryCount }
                : req
            )
          );
        }
      }
    }

    processingRef.current = false;
    setIsProcessing(false);

    // Call the callback after queue is processed
    if (onQueueProcessedRef.current) {
      onQueueProcessedRef.current();
    }
  }, [queue, isOnline, executeRequest, removeFromQueue]);

  // Process queue when coming back online
  useEffect(() => {
    if (isOnline && queue.length > 0 && !processingRef.current) {
      processQueue();
    }
  }, [isOnline, queue.length, processQueue]);

  const executeOrQueue = useCallback(
    async <T,>(
      requestFn: () => Promise<T>,
      queueData: Omit<QueuedRequest, 'id' | 'timestamp' | 'retryCount'>
    ): Promise<T | null> => {
      if (isOnline) {
        try {
          return await requestFn();
        } catch (error) {
          // If request fails while online, don't queue it
          throw error;
        }
      } else {
        // Add to queue if offline
        addToQueue(queueData);
        return null;
      }
    },
    [isOnline, addToQueue]
  );

  return {
    queue,
    isProcessing,
    addToQueue,
    removeFromQueue,
    executeOrQueue,
    queueLength: queue.length,
  };
};
